import {
    consoleLogger,
    CoreDI,
    IBrowser,
    IEventManagerWithAsync,
    lazyInject,
    LoadPriorityName,
    UpdateManager
} from '@gongxigames/framework/core';
import {PSGameClientWrapperEvents} from '@gongxigames/framework/integrations/pokerstars/PSGameClientWrapperEvents';
import {
    RG2XC, RG2XCError, RG2XCEventsMap,
    XC2RG, XC2RG_ERROR_HANDLED,
    XC2RG_PROPERTIES_UPDATED, XC2RGBalanceUpdated, XC2RGEventsMap,
    XC2RGPauseGame, XCClientMessage,
    XCLaunchOptions
} from '@gongxigames/framework/integrations/pokerstars/PSTypes';
import { EventEmitter } from '@pixi/utils';
import {
    BetInfo,
    BigWinEvents,
    BottomPanelEvents,
    FlowModuleEvents, KeypadControlsEvents,
    LogicEvents,
    RealityCheckEvents, RoundInfo, WinMeterFinishedValue
} from '@gongxigames/framework/slot';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

const XC2RG_MESSAGE_PREFIX = 'xc2rg';

const PauseOwner = {};
const BigWinOwner = {};

enum ActionType {
    NORMAL_SPIN = 'normalSpin',
    WITH_FEATURE = 'withFeature',
    ENDED_FEATURE = 'endedFeature'
}

export class XCClientAdapter {
    public static NAME = Symbol('XCClientAdapter');

    public isRestoredGame = false;

    @lazyInject(CoreDI.Browser)
    private browser!: IBrowser;

    @lazyInject(CoreDI.IEventManagerWithAsync)
    private eventsManager!: IEventManagerWithAsync<PSGameClientWrapperEvents>;

    @lazyInject(CoreDI.UpdateManager)
    private updateManager!: UpdateManager;

    private emitter: EventEmitter = new EventEmitter();
    private handStarted = false;
    private isShownResult = false;
    private isGameWonUpdated = false;
    private actionType: ActionType = ActionType.NORMAL_SPIN;
    private launchOptions: XCLaunchOptions = {
        countryCode: '',
        currency: '',
        gameServer: '',
        hostId: 0,
        isNewSession: false,
        languageCode: '',
        platform: 0,
        playForFun: false,
        rgToken: '',
        showPlayMoneyWithDecimal: false,
        siteId: 0,
        siteName: '',
        soundEnabled: false,
        userId: '',
        vendorGameConfig: ''
    };

    public init(options: XCLaunchOptions): void {
        this.launchOptions = options;
        this.setupEvents();
    }

    public setupEvents(): void {
        this.browser.addEventListener('message', this.receiveMessage.bind(this), false);

        this.addXC2RGEventHandler(XC2RG.ShowHelp, this.handleShowHelp);
        this.addXC2RGEventHandler(XC2RG.ShowPaytable, this.handleShowPaytable);
        this.addXC2RGEventHandler(XC2RG.PropertiesUpdated, this.handleXC2RGPropertiesUpdated);
        this.addXC2RGEventHandler(XC2RG.BalanceUpdated2, this.handleXC2RGBalanceUpdated);
        this.addXC2RGEventHandler(XC2RG.PauseGame, this.handlePauseGame);
        this.addXC2RGEventHandler(XC2RG.ResumeGame, this.handleResumeGame);
        this.addXC2RGEventHandler(XC2RG.CloseGame, this.handleCloseGame);
        this.addXC2RGEventHandler(XC2RG.ErrorHandled, this.handleXC2RGErrorHandled);

        this.eventsManager.on('General.Game.INITIALIZED', this.handleLaunchGameDone.bind(this), this);
        this.eventsManager.on('General.UI.BET.CHANGED', this.sendInitialBet.bind(this), this);
        this.eventsManager.on('General.Game.STARTED', this.handleGameStarted.bind(this), this);
        this.eventsManager.on('General.Game.STARTED', this.setupEventsAfterGameStart.bind(this), this);
        this.eventsManager.on('General.ResourcesLoading.START_LOAD_RESOURCES', this.handlePreloadStart.bind(this), this);
        this.eventsManager.on('General.ResourcesLoading.PROGRESS', this.handlePreloaderProgress.bind(this), this);
        this.eventsManager.on('General.ResourcesLoading.LOADED_RESOURCES', this.handlePreloaderEnd.bind(this), this);
        this.eventsManager.on(LogicEvents.RESPONSE, this.handleResponse.bind(this), this);
        this.eventsManager.on('PokerStartsErrorHandler.gameError', this.handleGameError.bind(this), this);
    }

    protected setupEventsAfterGameStart(): void {
        strictUiEmitter.on('UI.sounds.switchedTo', this.handleSwitchedSound.bind(this), {owner: this});
        this.eventsManager.on(RealityCheckEvents.QUIT_GAME_CLICKED, this.handleUIElementClicked.bind(this, 'lobby'), this);
        this.eventsManager.on(BottomPanelEvents.HOME_BUTTON_CLICKED, this.handleUIElementClicked.bind(this, 'lobby'), this);
        this.eventsManager.on(KeypadControlsEvents.HOME_BUTTON_CLICKED, this.handleUIElementClicked.bind(this, 'lobby'), this);
        strictUiEmitter.on('UI.command.home', this.handleUIElementClicked.bind(this, 'lobby'));
        this.eventsManager.on(BottomPanelEvents.CASHIER_CLICKED, this.handleUIElementClicked.bind(this, 'cashier'), this);
        strictUiEmitter.on('UI.menu.cashierClicked', this.handleUIElementClicked.bind(this, 'cashier'));
        this.eventsManager.on('General.Window.QUIT', this.handleUIElementClicked.bind(this, 'lobby'), this);
        this.eventsManager.on('General.Window.GO_TO_HOME', this.handleUIElementClicked.bind(this, 'lobby'), this);
        this.eventsManager.on('General.Window.OPEN_CASHIER', this.handleUIElementClicked.bind(this, 'cashier'), this);
        this.eventsManager.on(LogicEvents.BET_CHANGED, this.handleBetChanged.bind(this), this);
        this.eventsManager.on(LogicEvents.ROUND_STARTED, this.handleRoundStarted.bind(this), this);
        this.eventsManager.on('General.Flow.PAID_ROUND_ENDED', this.handleRoundEnded.bind(this), this);
        this.eventsManager.on( 'Autoplay.STARTED', this.handleStartedAutoplay.bind(this), this);
        this.eventsManager.on( 'Autoplay.STOPPED', this.handleStoppedAutoplay.bind(this), this);
        this.eventsManager.on(BigWinEvents.WIN_METER_FINISHED, this.onWinMeterFinished.bind(this), this);
        this.eventsManager.on(BigWinEvents.POST_FEATURE_BW_SKIPPED, this.onWinMeterFinished.bind(this), this);
        this.eventsManager.on('General.WinPresentation.WIN_METER_FINISHED', this.onWinMeterFinished.bind(this), this);
        this.eventsManager.on(FlowModuleEvents.BIG_WIN_AFTER_FREE_SPINS_STARTED, this.onBigWinAfterFeature.bind(this), this);
        this.eventsManager.on(FlowModuleEvents.BIG_WIN_AFTER_BONUS_GAME_STARTED, this.onBigWinAfterFeature.bind(this), this);
    }

    private onBigWinAfterFeature(): void {
        if (this.actionType === ActionType.ENDED_FEATURE) {
            this.eventsManager.on(BigWinEvents.WIN_METER_FINISHED, this.onBigWinFinishedAfterFeature.bind(this), BigWinOwner);
            this.eventsManager.on(BigWinEvents.POST_FEATURE_BW_SKIPPED, this.onBigWinFinishedAfterFeature.bind(this), BigWinOwner);
        }
    }

    private onBigWinFinishedAfterFeature(eventData: WinMeterFinishedValue): void {
        this.eventsManager.off(BigWinEvents.WIN_METER_FINISHED, BigWinOwner);
        this.eventsManager.off(BigWinEvents.POST_FEATURE_BW_SKIPPED, BigWinOwner);

        this.sendGameWonUpdated(eventData);
    }

    private onWinMeterFinished(eventData: WinMeterFinishedValue): void {
        if (this.actionType === ActionType.NORMAL_SPIN) {
            this.sendGameWonUpdated(eventData);
        }
    }

    private handleShowHelp(): void {
        this.eventsManager.emit(BottomPanelEvents.GAME_RULES_CLICKED);
        strictUiEmitter.emit('UI.menu.gameRulesClicked');
    }

    private handleShowPaytable(): void {
        strictUiEmitter.emit('UI.menu.infoPageClicked');
    }

    private handlePauseGame(event: XC2RGPauseGame): void {
        const {condition} = event;
        const onRoundEnded = () => {
            this.pauseGame();
            this.eventsManager.off('General.Flow.PAID_ROUND_ENDED', PauseOwner);
        };

        if (condition === 'waitUntilHandEnd') {
            if (this.handStarted) {
                this.eventsManager.on('General.Flow.PAID_ROUND_ENDED', onRoundEnded, PauseOwner);
            } else {
                this.pauseGame();
            }
        } else {
            consoleLogger.error('handlePauseGame for condition==waitUntilAnimationEnd not implemented!');
        }
    }

    private handleResumeGame(): void {
        this.resumeGame();

        if (this.eventsManager.hasListener('General.Flow.PAID_ROUND_ENDED', PauseOwner)) {
            this.eventsManager.off('General.Flow.PAID_ROUND_ENDED', PauseOwner);
        }
    }

    private handleCloseGame(): void {
        this.sendMessageToXCClient(RG2XC.GameReadyForUnload, {localizedMessage: ''});
    }

    private handleXC2RGPropertiesUpdated(data: XC2RG_PROPERTIES_UPDATED): void {
        const {soundEnabled} = data.keysAndValues;

        if (soundEnabled !== undefined) {
            strictUiEmitter.offByOwner('UI.sounds.switchedTo', this);
            strictUiEmitter.emit(soundEnabled ? 'UI.sounds.enable' : 'UI.sounds.disable');
            strictUiEmitter.on('UI.sounds.switchedTo', this.handleSwitchedSound.bind(this), {owner: this});
        }
    }

    private handleXC2RGBalanceUpdated(data: XC2RGBalanceUpdated): void {
        const {balance} = data;

        if (balance !== undefined) {
            this.eventsManager.emit('XCAPIEvents.balanceUpdated', balance);
        }
    }

    private handleUIElementClicked(elementId: string): void {
        this.sendMessageToXCClient(RG2XC.UIElementClicked, {elementId});
    }

    private handleGameStarted(): void {
        if (!this.launchOptions.isNewSession && this.isRestoredGame) {
            this.handleRoundStarted();
        }
    }

    private handlePreloadStart(loaderPriority: LoadPriorityName): void {
        if (loaderPriority === 'default') {
            this.sendMessageToXCClient(RG2XC.PreloadStart);
        }
    }

    private handlePreloaderProgress(percentage: number): void {
        this.sendMessageToXCClient(RG2XC.PreloaderProgress, {
            percentage: (Math.round((percentage + Number.EPSILON) * 100) / 100),
            localizedText: ''
        });
    }

    private handlePreloaderEnd(loaderPriority: LoadPriorityName): void {
        if (loaderPriority === 'default') {
            this.sendMessageToXCClient(RG2XC.PreloaderEnd);
        }
    }

    private handleLaunchGameDone(): void {
        this.sendMessageToXCClient(RG2XC.LaunchGameDone);
    }

    private handleSwitchedSound(soundEnabled: boolean): void {
        this.sendMessageToXCClient(RG2XC.PropertiesUpdated, { soundEnabled });
    }

    private handleBetChanged(eventData: {totalBet: number;}): void {
        this.sendMessageToXCClient(RG2XC.GameWagerUpdated, {value: eventData.totalBet});
    }

    private handleResponse(data: { nextActionIsSpin: boolean; previousActionIsSpin: boolean;}): void {
        const isNormalSpin = data.nextActionIsSpin && data.previousActionIsSpin;
        const inFeatureEndSpin = data.nextActionIsSpin && !data.previousActionIsSpin;

        this.actionType = isNormalSpin ? ActionType.NORMAL_SPIN : (inFeatureEndSpin ? ActionType.ENDED_FEATURE : ActionType.WITH_FEATURE);
    }

    private handleRoundStarted(): void {
        if (this.actionType === ActionType.NORMAL_SPIN || this.actionType === ActionType.ENDED_FEATURE) {
            this.sendMessageToXCClient(RG2XC.GameStatusUpdated, {status: 'handStart'});
            this.sendMessageToXCClient(RG2XC.GameWonUpdated, {value: 0});
        }

        this.handStarted = true;
        this.isShownResult = false;
        this.isGameWonUpdated = false;
    }

    private handleGameError(error: RG2XCError): void {
        this.sendMessageToXCClient(RG2XC.ErrorOccurred, error);

        this.eventsManager.emit('General.UI.DISABLE', XCClientAdapter.NAME);
        this.eventsManager.emit('General.UI.DISABLE_KEYBOARD', XCClientAdapter.NAME);
    }

    private handleXC2RGErrorHandled(xcError: XC2RG_ERROR_HANDLED): void {
        this.eventsManager.emit('General.UI.ENABLE', XCClientAdapter.NAME);
        this.eventsManager.emit('General.UI.ENABLE_KEYBOARD', XCClientAdapter.NAME);

        if (xcError.error === 'genericXCError') {
            this.eventsManager.emit('Autoplay.FORCE_STOP');
        }
    }

    private handleRoundEnded(roundInfo: RoundInfo): void {
        if (this.actionType === ActionType.NORMAL_SPIN || this.actionType === ActionType.ENDED_FEATURE) {
            this.sendGameWonUpdated(roundInfo);
            this.sendMessageToXCClient(RG2XC.GameStatusUpdated, {status: 'handEnd'});
            this.handStarted = false;
        }
    }

    private handleStartedAutoplay(): void {
        this.sendMessageToXCClient(RG2XC.GameStatusUpdated, {status: 'autoPlayStart'});
    }

    private handleStoppedAutoplay(): void {
        this.sendMessageToXCClient(RG2XC.GameStatusUpdated, {status: 'autoPlayEnd'});
    }

    private sendGameWonUpdated(data: RoundInfo): void {
        if (!this.isGameWonUpdated && (this.actionType === ActionType.NORMAL_SPIN || this.actionType === ActionType.ENDED_FEATURE)) {
            this.isGameWonUpdated = true;

            if (data.totalWin > 0) {
                this.sendMessageToXCClient(RG2XC.GameWonUpdated, {value: data.totalWin});
            }

            this.gameResultShown();
        }
    }

    private gameResultShown(): void {
        if (!this.isShownResult) {
            this.isShownResult = true;
            this.sendMessageToXCClient(RG2XC.GameResultShown);
        }
    }

    private pauseGame(): void {
        this.sendMessageToXCClient(RG2XC.GamePaused);
        this.eventsManager.emitAsync('General.System.RESUME');
    }

    private resumeGame(): void {
        this.sendMessageToXCClient(RG2XC.GameResumed);
        this.eventsManager.emitAsync('General.System.RESUME');
    }

    private sendInitialBet(betInfo: BetInfo): void {
        this.handleBetChanged(betInfo);
        this.eventsManager.off('General.UI.BET.CHANGED', this);
    }

    private sendMessageToXCClient<K extends keyof RG2XCEventsMap>(msgId: K, ...data: RG2XCEventsMap[K][]): void {
        const [values = {}] = data;

        this.browser.postMessageToParent(JSON.stringify({msgId, ...values}));
    }

    private receiveMessage(event: any): void {
        if (!event.data && typeof event.data === 'string') {
            return;
        }

        try {
            const data = JSON.parse(event.data);

            if (!data.msgId) {
                return;
            }

            const {msgId} = data;

            if (msgId.includes(XC2RG_MESSAGE_PREFIX)) {
                this.handleXC2RGEvent(data);
            }
        } catch (e) {
            consoleLogger.error('Error parse message or error call handleXC2RGEvent');
            consoleLogger.error(e);
        }
    }

    private handleXC2RGEvent(eventData: XCClientMessage): void {
        const {msgId} = eventData;

        this.emitter.emit(msgId, eventData);
    }

    private addXC2RGEventHandler<K extends keyof XC2RGEventsMap>(eventName: K, handler: (args: XC2RGEventsMap[K]) => void): void {
        this.emitter.on(eventName, handler, this);
    }
}
